import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ShieldCheck, Truck, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/products/${slug}`);
        if (!cancelled) setP(r.data);
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <main className="max-w-[1400px] mx-auto px-5 py-20 font-mono-tech text-xs text-steel-400">
        LOADING UNIT…
      </main>
    );
  }
  if (error || !p) {
    return (
      <main className="max-w-[1400px] mx-auto px-5 py-20 text-center">
        <div className="font-display text-4xl mb-4">Unit not found.</div>
        <button onClick={() => nav("/catalog")} className="btn-copper">
          <ArrowLeft size={14} /> Back to catalog
        </button>
      </main>
    );
  }

  const specs = [
    ["SKU", p.sku],
    ["Category", p.category.replace("-", " ")],
    ["Voltage", p.voltage],
    ["Power", `${p.power_kw} kW`],
    ["Teeth", p.teeth || "—"],
    ["Rotation", p.rotation || "—"],
    ["Condition", p.condition],
  ];

  return (
    <main data-testid="product-detail-page" className="border-b border-ink-600">
      <div className="max-w-[1400px] mx-auto grid grid-cols-12 border-x border-ink-600">
        {/* Image */}
        <div className="col-span-12 md:col-span-7 border-r border-ink-600 relative bg-ink-800">
          <div className="aspect-[4/3] md:aspect-auto md:h-full relative overflow-hidden">
            <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
            <div className="absolute top-5 left-5 bg-ink-900/80 border border-ink-600 px-3 py-2 font-mono-tech text-[10px]">
              {p.in_stock ? <span className="text-emerald-400">● IN STOCK</span> : <span className="text-amber-400">● BUILD TO ORDER</span>}
            </div>
            <div className="absolute bottom-5 left-5 font-mono-tech text-[9px] text-steel-500">
              WRE / WORKSHOP / {p.sku}
            </div>
          </div>
        </div>

        {/* Detail */}
        <div className="col-span-12 md:col-span-5 p-6 md:p-10 flex flex-col">
          <Link to="/catalog" className="font-mono-tech text-[10px] text-steel-400 hover:text-copper inline-flex items-center gap-1 mb-6">
            <ArrowLeft size={12} /> BACK TO CATALOG
          </Link>

          <div className="font-mono-tech text-[10px] text-copper mb-3">
            {p.vehicle_make} · {p.voltage} · {p.power_kw} kW
          </div>
          <h1 className="font-display text-3xl md:text-5xl leading-[0.95] mb-5">{p.name}</h1>
          <p className="text-steel-300 text-sm leading-relaxed mb-8">{p.description}</p>

          <div className="grid grid-cols-2 gap-px bg-ink-600 border border-ink-600 mb-8">
            {specs.map(([k, v]) => (
              <div key={k} className="bg-ink-900 p-4">
                <div className="font-mono-tech text-[9px] text-steel-500 mb-1">{k}</div>
                <div className="text-sm capitalize">{v}</div>
              </div>
            ))}
          </div>

          {p.vehicle_models?.length > 0 && (
            <div className="mb-8">
              <div className="font-mono-tech text-[10px] text-copper mb-3">FITMENT</div>
              <ul className="space-y-2">
                {p.vehicle_models.map((m) => (
                  <li key={m} className="text-sm flex items-center gap-2">
                    <Check size={14} className="text-copper" /> {m}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-auto border-t border-ink-600 pt-6">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="font-mono-tech text-[9px] text-steel-500">PRICE FROM</div>
                <div className="font-display text-5xl text-copper">£{p.price_gbp.toFixed(0)}</div>
                <div className="font-mono-tech text-[9px] text-steel-500 mt-1">INC. VAT · CORE EXCHANGE ELIGIBLE</div>
              </div>
              <div className="flex flex-col gap-1 text-right font-mono-tech text-[9px] text-steel-400">
                <span className="flex items-center justify-end gap-1"><Truck size={12} className="text-copper" /> NEXT-DAY UK</span>
              </div>
            </div>

            <Link
              to={`/quote?sku=${p.sku}`}
              data-testid="product-quote-cta"
              className="btn-copper w-full justify-between !px-6 !py-4"
            >
              Request Fixed-Price Quote <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
